import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, Eraser, PenLine, Loader2, ArrowLeft } from "lucide-react";
import { fmtMoney, proposalTotals, providerDocHref, fileUrl } from "../lib/api";
import PrivacyNotice from "../components/PrivacyNotice";
import useSignaturePad from "../hooks/useSignaturePad";
import { useTr } from "../lib/publicI18n";
import { DocumentsBlock } from "./PublicProposal";

const newRequestId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);

const docsKey = (pr) => JSON.stringify((pr.documents || []).map((d) => [d.file_id || null, d.url || null, d.sha256 || null]).sort());

/**
 * Feuille de signature d'un LOT : une seule signature, un seul nom, un seul consentement pour les villas cochées ; chaque villa garde ce
 * qui lui est propre (numéro de fibre, mandat de portabilité, paiement à l'année). Un seul balisage piloté par `premium`.
 * Le serveur reste juge (tout ou rien) : ici on évite seulement d'envoyer ce qui est manifestement incomplet.
 */
export default function GroupSignatureSheet({
  villas, terms, brandName, premium, groupId, deciding, onClose, onSign, onDocsUploaded, requiredDocuments, documents, amountsLine = "", ht = "", titles = {},
}) {
  const tr = useTr();
  const pad = useSignaturePad();
  const requestId = useRef(newRequestId()); // un identifiant par ouverture : un nouvel essai après échec réseau rejoue la MÊME demande
  const [name, setName] = useState("");
  const [fibre, setFibre] = useState({});
  // On retient le TEXTE du mandat coché, pas un booléen : s'il change pendant la lecture (409 puis rechargement), la case se décoche seule.
  const [mandate, setMandate] = useState({});
  const [annual, setAnnual] = useState({});
  const [consent, setConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [providersAccepted, setProvidersAccepted] = useState(() => new Set());
  const [express, setExpress] = useState({}); // demande de démarrage anticipé : UNE case par villa, jamais cochée d'office
  const panelRef = useRef(null);
  const [error, setError] = useState("");
  const [villaErrors, setVillaErrors] = useState({});

  // Clavier : Échap ferme la feuille (sauf pendant l'envoi) ; le focus entre dans la boîte de dialogue à l'ouverture.
  useEffect(() => { panelRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !deciding) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deciding, onClose]);

  const askFibre = (v) => v.fibre_number_required && !v.fibre_number;
  const porting = (v) => v.phone_line?.mode === "portabilite";
  const mandateChecked = (v) => porting(v) && !!v.phone_line.mandate_text && mandate[v.public_id] === v.phone_line.mandate_text;
  const consumerLike = villas.some((v) => v.legal?.consumer_like);

  // Prestataires : une case COMMUNE seulement si l'id ET les documents sont identiques sur toutes les villas qui le portent, sinon une case par villa.
  const providerGroups = useMemo(() => {
    const byId = new Map();
    villas.forEach((v) => (v.providers || []).forEach((pr) => byId.set(pr.id, [...(byId.get(pr.id) || []), { villa: v, pr }])));
    const out = [];
    byId.forEach((list, id) => {
      if (list.every((x) => docsKey(x.pr) === docsKey(list[0].pr))) out.push({ key: id, pr: list[0].pr, villas: list.map((x) => x.villa) });
      else list.forEach((x) => out.push({ key: `${id}-${x.villa.public_id}`, pr: x.pr, villas: [x.villa] }));
    });
    return out;
  }, [villas]);
  const toggleProvider = (key) => setProvidersAccepted((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const missingProvider = providerGroups.find((g) => !providersAccepted.has(g.key));

  const termsUsed = useMemo(() => {
    const ids = new Set(villas.map((v) => v.terms?.id).filter(Boolean));
    return (terms || []).filter((t) => ids.has(t.id));
  }, [villas, terms]);
  const hasTerms = termsUsed.length > 0;

  const missingDocs = (requiredDocuments || []).filter((rd) => !(documents || []).some((d) => d.type === rd.type));
  const missingFibre = villas.some((v) => askFibre(v) && !(fibre[v.public_id] || "").trim());
  const missingMandate = villas.some((v) => porting(v) && !mandateChecked(v));
  const blocked = villas.length === 0 || (hasTerms && !termsAccepted) || !!missingProvider || missingDocs.length > 0 || missingFibre || missingMandate;

  const submit = async () => {
    setError("");
    setVillaErrors({});
    if (missingDocs.length) return setError(tr(`Déposez d'abord vos pièces : ${missingDocs.map((d) => d.label).join(", ")}.`, `Please upload your documents first: ${missingDocs.map((d) => d.label).join(", ")}.`));
    if (missingFibre) return setError(tr("Indiquez le numéro de fibre de chaque villa concernée.", "Please enter the fibre line number of each property concerned."));
    if (missingMandate) return setError(tr("Cochez le mandat de portabilité de chaque villa concernée.", "Please tick the number porting authorisation of each property concerned."));
    if (!name.trim()) return setError(tr("Indiquez votre nom complet.", "Please enter your full name."));
    if (!pad.hasDrawn()) return setError(tr("Veuillez dessiner votre signature.", "Please draw your signature."));
    if (!consent) return setError(tr("Vous devez accepter la valeur juridique de la signature.", "You must accept the legal value of the signature."));
    if (hasTerms && !termsAccepted) return setError(tr("Vous devez accepter les conditions générales de vente.", "You must accept the general terms and conditions of sale."));
    if (missingProvider) return setError(tr(`Vous devez accepter les conditions de ${missingProvider.pr.name}.`, `You must accept the ${missingProvider.pr.name} terms.`));

    const perProposal = {};
    for (const v of villas) {
      const accepted = providerGroups.filter((g) => g.villas.includes(v) && providersAccepted.has(g.key)).map((g) => g.pr.id);
      perProposal[v.public_id] = {
        content_hash: v.content_hash,
        ...(askFibre(v) ? { fibre_number: fibre[v.public_id].trim() } : {}),
        ...(v.annual_payment_offered && annual[v.public_id] ? { annual_payment: annual[v.public_id] } : {}), // choix périmé (offre sans TV depuis) : jamais envoyé
        ...(express[v.public_id] && v.legal?.consumer_like ? { express_start: true } : {}),
        ...(porting(v) ? { phone_mandate_accepted: true, phone_mandate_text_hash: v.phone_line.mandate_text_hash } : {}),
        ...(accepted.length ? { providers_accepted: accepted } : {}),
      };
    }
    const r = await onSign({
      decision: "accepted", request_id: requestId.current, public_ids: villas.map((v) => v.public_id), signer_name: name.trim(),
      signature_data: pad.toDataURL(), consent: true, terms_accepted: termsAccepted, terms_version_ids: termsUsed.map((t) => t.id), per_proposal: perProposal,
    });
    if (r && r.ok === false) {
      const flagged = Object.keys(r.villas || {}).length > 0;
      setError(r.general || (flagged ? tr("Certaines propositions ne peuvent pas être signées :", "Some proposals could not be signed:") : r.message) || tr("Une erreur est survenue.", "An error occurred."));
      setVillaErrors(r.villas || {});
      // Après un 409 la page recharge le dossier : les cases de mandat des villas fautives (ou de toutes si le serveur n'en désigne aucune) sont vidées.
      if (r.status === 409) {
        const flagged = Object.keys(r.villas || {});
        setMandate((cur) => {
          if (!flagged.length) return {};
          const next = { ...cur };
          flagged.forEach((id) => delete next[id]);
          return next;
        });
      }
    }
  };

  const ui = premium
    ? {
      shell: "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4",
      panel: "h-full sm:h-auto sm:max-h-[92vh] w-full sm:max-w-lg overflow-y-auto bg-[#ECE9E3] sm:rounded-[22px] animate-fade-up",
      card: "rounded-[18px] bg-white p-4 space-y-3",
      label: "text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground",
      input: "mt-1.5 w-full h-12 px-3.5 rounded-xl border-[1.5px] border-[#DAD5CC] bg-white text-base outline-none focus:border-primary",
      canvas: "w-full h-40 rounded-[14px] border-[1.5px] border-dashed border-[#CFC9BE] bg-[#FBFAF7] touch-none cursor-crosshair",
      submit: "w-full h-14 rounded-[14px] bg-primary text-white text-[16.5px] font-bold flex items-center justify-center gap-2.5 hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed",
      box: "w-[22px] h-[22px] mt-0.5 shrink-0",
    }
    : {
      shell: "fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm sm:p-4",
      panel: "bg-white sm:rounded-sm shadow-2xl w-full sm:max-w-md h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto px-5 pt-3 pb-8 sm:p-6 animate-fade-up",
      card: "border border-border rounded-sm p-3 space-y-3",
      label: "text-sm font-medium",
      input: "w-full mt-1.5 h-11 px-3 border border-border rounded-sm outline-none focus:border-primary",
      canvas: "w-full h-40 border-2 border-dashed border-border rounded-sm bg-surface touch-none cursor-crosshair",
      submit: "sm:flex-1 h-11 bg-primary text-white rounded-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed",
      box: "mt-0.5 w-5 h-5 shrink-0",
    };
  const accent = { accentColor: "hsl(var(--primary))" };
  const small = premium ? "text-[13px] leading-relaxed" : "text-[13px] sm:text-xs text-muted-foreground";
  const n = villas.length;

  const body = (
    <>
      <div className={premium ? "pt-1" : ""}>
        <h2 id="group-sheet-title" className={premium ? "font-display font-extrabold text-[1.75rem] leading-[1.05] tracking-[-0.02em]" : "font-heading text-xl font-bold"}>
          {tr(`Signer ${n} proposition${n > 1 ? "s" : ""}`, `Sign ${n} proposal${n > 1 ? "s" : ""}`)}
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5">
          {tr("Une seule signature, appliquée à chaque contrat. Chaque contrat garde sa propre preuve.", "One signature, applied to each contract. Each contract keeps its own proof.")}
        </p>
        {amountsLine && <p className="text-sm font-semibold mt-1.5" data-testid="group-sheet-amounts">{amountsLine}</p>}
      </div>

      {(requiredDocuments || []).length > 0 && (
        <div className={ui.card}>
          <p className={ui.label}>{tr("Vos pièces", "Your documents")} <span className="font-normal normal-case tracking-normal text-muted-foreground">{tr("— obligatoires pour signer", "— required to sign")}</span></p>
          <DocumentsBlock variant="sheet" endpoint={`/public/groups/${groupId}/documents`} requiredDocuments={requiredDocuments} documents={documents} onUploaded={onDocsUploaded} />
        </div>
      )}

      <div className="space-y-2.5">
        {villas.map((v) => {
          const t = proposalTotals(v.sections);
          const pid = v.public_id;
          return (
            <div key={pid} className={ui.card} data-testid={`group-sheet-villa-${pid}`}>
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-semibold text-sm min-w-0 truncate">{v.title}</p>
                <p className="text-xs text-muted-foreground shrink-0">
                  {[t.recurring > 0 && `${fmtMoney(t.recurring, v.currency)}${ht ? ` ${ht}` : ""}${tr("/mois", "/month")}`, t.oneShot > 0 && `${fmtMoney(t.oneShot, v.currency)}${ht ? ` ${ht}` : ""}`].filter(Boolean).join(" · ")}
                </p>
              </div>
              {v.fibre_number_required && (askFibre(v) ? (
                <div>
                  <label htmlFor={`sig-fibre-${pid}`} className={premium ? "text-[13px] font-semibold" : "text-sm font-medium"}>
                    {tr("Numéro de fibre", "Fibre line number")} <span className="font-normal text-muted-foreground">{tr("— obligatoire pour signer", "— required to sign")}</span>
                  </label>
                  <input id={`sig-fibre-${pid}`} value={fibre[pid] || ""} maxLength={100} data-testid={`group-sig-fibre-${pid}`}
                    onChange={(e) => setFibre((c) => ({ ...c, [pid]: e.target.value }))} placeholder={tr("Votre numéro de fibre", "Your fibre line number")} className={ui.input} />
                </div>
              ) : (
                <p className="text-sm" data-testid={`group-sig-fibre-known-${pid}`}>{tr("Numéro de fibre", "Fibre line number")} : <strong>{v.fibre_number}</strong></p>
              ))}
              {v.phone_line && (porting(v) ? (
                <div className="space-y-2" data-testid={`group-sig-phone-${pid}`}>
                  <p className="text-sm">{tr("Ligne téléphonique : portabilité de votre numéro", "Telephone line: porting of your number")} <strong>{v.phone_line.number_display}</strong></p>
                  <label className="flex items-start gap-3 cursor-pointer min-h-10">
                    <input type="checkbox" checked={mandateChecked(v)} data-testid={`group-sig-mandate-${pid}`} className={ui.box} style={accent}
                      onChange={(e) => setMandate((c) => ({ ...c, [pid]: e.target.checked ? v.phone_line.mandate_text : "" }))} />
                    <span className={`${small} whitespace-pre-wrap`}>{v.phone_line.mandate_text}</span>
                  </label>
                </div>
              ) : (
                <p className="text-sm" data-testid={`group-sig-phone-new-${pid}`}>{tr("Ligne téléphonique : un nouveau numéro vous sera attribué après la signature.", "Telephone line: a new number will be assigned to you after signing.")}</p>
              ))}
              {v.annual_payment_offered && (
                <p className={premium ? "text-[13px] font-semibold" : "text-sm font-medium"}>{tr("Paiement souhaité", "Preferred payment")} <span className="font-normal text-muted-foreground">{tr("— les prix restent mensuels", "— prices stay monthly")}</span></p>
              )}
              {v.annual_payment_offered && (
                <div role="radiogroup" aria-label={tr(`Paiement — ${v.title}`, `Payment — ${v.title}`)} className="flex flex-wrap gap-2">
                  {[[null, tr("Au mois", "Monthly")], ["tv", tr("La TV à l'année", "TV billed yearly")], ["all", tr("Toute l'offre à l'année", "Everything billed yearly")]].map(([val, label]) => {
                    const on = (annual[pid] || null) === val;
                    return (
                      <button key={val || "month"} type="button" role="radio" aria-checked={on} data-testid={`group-sig-annual-${pid}-${val || "none"}`}
                        onClick={() => setAnnual((c) => ({ ...c, [pid]: val }))}
                        className={`px-3 min-h-10 text-xs font-semibold border-2 transition-colors ${premium ? "rounded-xl" : "rounded-sm"} ${on ? "border-primary bg-primary text-white" : "border-border bg-background hover:border-primary/60"}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              {v.legal?.consumer_like && (
                <label className="flex items-start gap-3 cursor-pointer min-h-10">
                  <input type="checkbox" checked={!!express[pid]} onChange={(e) => setExpress((c) => ({ ...c, [pid]: e.target.checked }))} data-testid={`group-sig-express-${pid}`} className={ui.box} style={accent} />
                  <span className={small}>{tr("Je demande que le service de cette villa commence avant la fin du délai de rétractation.", "I request that the service for this property begins before the end of the withdrawal period.")}</span>
                </label>
              )}
              {villaErrors[pid] && <p className="text-sm font-medium text-rose-700" role="alert" data-testid={`group-sig-villa-error-${pid}`}>{villaErrors[pid]}</p>}
            </div>
          );
        })}
      </div>

      {(hasTerms || providerGroups.length > 0 || consumerLike) && (
        <div className={ui.card}>
          <p className={ui.label}>{tr("Vos conditions", "Your terms")}</p>
          {hasTerms && (
            <label className="flex items-start gap-3 cursor-pointer min-h-10" data-testid="group-sig-terms-wrapper">
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} data-testid="group-sig-terms" className={ui.box} style={accent} />
              <span className={small}>
                {tr(`J'ai lu et j'accepte les conditions générales de vente${brandName ? ` de ${brandName}` : ""}, valables pour ${n > 1 ? `ces ${n} contrats` : "ce contrat"}`,
                  `I have read and accept the general terms and conditions of sale${brandName ? ` of ${brandName}` : ""}, valid for ${n > 1 ? `these ${n} contracts` : "this contract"}`)}
                {" "}({termsUsed.map((t) => `version ${t.version_label}`).join(", ")}{tr("", ", French version prevailing")}).
              </span>
            </label>
          )}
          {termsUsed.some((t) => t.format === "pdf" && t.file_id) && (
            <div className="flex flex-wrap gap-2 -mt-1">
              {termsUsed.map((t) => t.format === "pdf" && t.file_id && (
                <a key={t.id} href={fileUrl(t.file_id)} target="_blank" rel="noopener noreferrer" data-testid={`group-sig-terms-pdf-${t.id}`}
                  className="inline-flex items-center h-10 px-3 rounded-lg bg-muted text-xs font-bold hover:brightness-95 transition">{tr("Lire les CGV", "Read the terms")} (PDF) — v{t.version_label}</a>
              ))}
            </div>
          )}
          {providerGroups.map((g) => (
            <div key={g.key} className="flex items-start gap-3">
              <input id={`sig-provider-${g.key}`} type="checkbox" checked={providersAccepted.has(g.key)} onChange={() => toggleProvider(g.key)}
                data-testid={`group-sig-provider-${g.key}`} className={ui.box} style={accent} />
              <div className="flex-1 min-w-0">
                <label htmlFor={`sig-provider-${g.key}`} className={`${small} cursor-pointer block`}>
                  {tr("J'ai lu et j'accepte les conditions de", "I have read and accept the terms of")} <strong>{g.pr.name}</strong>
                  {g.villas.length === 1 && n > 1 ? ` (${g.villas[0].title})` : ""}, {tr("qui assure :", "who provides:")} {g.pr.lines.join(", ")}.
                </label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {(g.pr.documents || []).map((d) => providerDocHref(d) && (
                    <a key={d.file_id || d.url} href={providerDocHref(d)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center h-10 px-3 rounded-lg bg-muted text-xs font-bold hover:brightness-95 transition">{d.label}</a>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {consumerLike && (
            <div className="p-3 rounded-sm bg-amber-50 border border-amber-200" data-testid="group-sig-withdrawal">
              <p className="text-[13px] text-amber-800">
                {tr(`Vous disposez d'un délai de rétractation de ${villas.find((v) => v.legal?.consumer_like)?.legal?.withdrawal_days || 14} jours à compter de la signature de chaque contrat (formulaire type sur la page du dossier).`,
                  `You have a ${villas.find((v) => v.legal?.consumer_like)?.legal?.withdrawal_days || 14}-day withdrawal period from the signature of each contract (model form on the file page).`)}
              </p>
            </div>
          )}
        </div>
      )}

      <div className={ui.card}>
        <p className={ui.label}>{tr("Votre signature", "Your signature")}</p>
        <div>
          <label htmlFor="group-sig-name" className={premium ? "text-[13px] font-semibold" : "text-sm font-medium"}>{tr("Nom complet", "Full name")}</label>
          <input id="group-sig-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-sig-name" autoComplete="name"
            placeholder={tr("Prénom Nom", "First name Last name")} className={ui.input} />
        </div>
        <div className="flex items-center justify-between">
          <span className={premium ? "text-[13px] font-semibold" : "text-sm font-medium"}>{tr("Signez avec le doigt", "Sign with your finger")}</span>
          <button type="button" onClick={pad.clear} data-testid="group-sig-clear" className="flex items-center gap-1 h-10 px-2 text-xs text-muted-foreground hover:text-foreground">
            <Eraser className="w-3.5 h-3.5" /> {tr("Effacer", "Clear")}
          </button>
        </div>
        <canvas ref={pad.canvasRef} width={400} height={160} data-testid="group-sig-canvas" aria-label={tr("Zone de signature", "Signature area")} className={ui.canvas} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer px-1 min-h-10">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="group-sig-consent" className={ui.box} style={accent} />
        <span className={premium ? "text-[13.5px] leading-relaxed" : small}>
          {n > 1
            ? tr(`J'accepte ces ${n} propositions et reconnais que ma signature électronique a valeur d'engagement pour chacune. Un exemplaire signé (PDF) de chaque contrat vous est envoyé par email.`,
              `I accept these ${n} proposals and acknowledge that my electronic signature is legally binding for each. A signed copy (PDF) of each contract will be sent to you by email.`)
            : tr("J'accepte cette proposition et reconnais que ma signature électronique a valeur d'engagement. Un exemplaire signé (PDF) vous est envoyé par email.",
              "I accept this proposal and acknowledge that my electronic signature is legally binding. A signed copy (PDF) will be sent to you by email.")}
        </span>
      </label>

      {error && <p className="text-sm font-medium text-rose-700 px-1" role="alert" data-testid="group-sig-error">{error}</p>}
      {/* Une villa désignée par le serveur mais plus dans la sélection (signée ailleurs, retirée) : son message n'a plus de carte, il est dit ici. */}
      {Object.entries(villaErrors).filter(([pid]) => !villas.some((v) => v.public_id === pid)).map(([pid, msg]) => (
        <p key={pid} className="text-sm font-medium text-rose-700 px-1" role="alert" data-testid={`group-sig-orphan-error-${pid}`}>{titles[pid] || pid} : {msg}</p>
      ))}
      {villas.length === 0 && <p className="text-sm text-muted-foreground px-1" data-testid="group-sig-empty">{tr("Plus aucune proposition à signer dans la sélection.", "No proposal left to sign in the selection.")}</p>}

      <p className="text-[11.5px] text-muted-foreground leading-relaxed px-1">
        {tr("En signant, vos nom, signature et adresse IP sont enregistrés comme preuve.", "When you sign, your name, signature and IP address are recorded as proof.")}{" "}
        <PrivacyNotice companyName={brandName} testIdSuffix="-group-signature" className="underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
      </p>

      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <button type="button" onClick={onClose} disabled={deciding} data-testid="group-sig-cancel"
          className="h-11 sm:px-5 rounded-sm text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">{tr("Annuler", "Cancel")}</button>
        <button type="button" onClick={submit} disabled={deciding || blocked} data-testid="group-sig-submit" className={ui.submit}>
          {deciding ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={2.5} />} {tr("Confirmer la signature", "Confirm signature")}
        </button>
      </div>
    </>
  );

  return (
    <div className={ui.shell} data-testid="group-sheet" role="dialog" aria-modal="true" aria-labelledby="group-sheet-title">
      <div className={`${ui.panel} outline-none`} ref={panelRef} tabIndex={-1}>
        <div className={`flex items-center justify-between ${premium ? "sticky top-0 z-10 bg-[#ECE9E3]/95 backdrop-blur px-5 pt-2 pb-2" : "mb-1"}`}>
          {premium ? (
            <button type="button" onClick={onClose} disabled={deciding} aria-label={tr("Retour au dossier", "Back to the file")} data-testid="group-sig-back"
              className="w-11 h-11 -ml-2.5 rounded-xl flex items-center justify-center hover:bg-black/5 transition-colors"><ArrowLeft className="w-5 h-5" /></button>
          ) : (
            <span className="flex items-center gap-2 text-primary"><PenLine className="w-5 h-5" /></span>
          )}
          {!premium && (
            <button type="button" onClick={onClose} disabled={deciding} aria-label={tr("Fermer", "Close")} data-testid="group-sig-close"
              className="w-11 h-11 -mr-2.5 shrink-0 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><X className="w-5 h-5" /></button>
          )}
        </div>
        <div className={premium ? "px-5 pb-8 space-y-3.5" : "space-y-4"}>{body}</div>
      </div>
    </div>
  );
}
